// components/Construction/ConstructionList.tsx
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
  useMediaQuery,
  Box,
  Collapse,
  TablePagination,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Construction } from '../../types/construction';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ReportListModal } from '../DailyReport/ReportListModal';
import CompletedWorksWithoutInvoiceSection from '../../components/Construction/CompletedWorksWithoutInvoiceSection';
import { useState, useEffect, useRef, useCallback } from 'react';

// モバイル無限スクロール: 初回表示件数 + 1回あたりロード件数
const MOBILE_INITIAL_COUNT = 5;
const MOBILE_LOAD_MORE_COUNT = 5;


interface ConstructionListProps {
  constructions: Construction[];
  loading: boolean;
  error: string | null;
  page: number;
  rowsPerPage: number;
  expandedRows: number[];
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleRowExpansion: (index: number) => void;
  onNewClick: () => void;
  onDailyReportCreate: (id: number) => void;
  onCalculationSheet: (id: number) => void; // add 20250305
  onEditReport: (id: number) => void; // 追加: 日報編集ハンドラー
  onInvoiceRequestClick: (id: number) => void; // 追加: 請求書作成依頼ハンドラー
}

export const ConstructionList: React.FC<ConstructionListProps> = ({
  constructions,
  loading,
  error,
  page,
  rowsPerPage,
  expandedRows,
  onEdit,
  onDelete,
  onPageChange,
  onRowsPerPageChange,
  onToggleRowExpansion,
  onNewClick,
  onDailyReportCreate,
  onCalculationSheet,
  onEditReport,
  onInvoiceRequestClick, // 追加: 請求書作成依頼ハンドラー
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // モーダル表示用のステートを追加
  const [reportListModalOpen, setReportListModalOpen] = useState(false);
  const [selectedConstructionId, setSelectedConstructionId] = useState<number | null>(null);

  // モバイル無限スクロール: 表示件数（リスト変更時にリセット）
  const [mobileVisibleCount, setMobileVisibleCount] = useState(MOBILE_INITIAL_COUNT);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // 検索やデータ更新で件数が変わったら先頭に戻す
  useEffect(() => {
    setMobileVisibleCount(MOBILE_INITIAL_COUNT);
  }, [constructions]);

  // sentinel が表示領域に入ったら次の 5 件を読み込むコールバック ref
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!isSmallScreen || !node) return;
      observerRef.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            setMobileVisibleCount(prev =>
              Math.min(prev + MOBILE_LOAD_MORE_COUNT, constructions.length)
            );
          }
        },
        { rootMargin: '120px' } // 余白手前で先読み
      );
      observerRef.current.observe(node);
    },
    [isSmallScreen, constructions.length]
  );

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  // 日報一覧表示ハンドラー
  const handleViewReportList = (constructionId: number) => {
    setSelectedConstructionId(constructionId);
    setReportListModalOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  // スクロール付きのページ変更ハンドラーを追加
  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    // 画面上部へスクロール
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // スムーズなスクロールを実現
    });

    // 既存のページ変更処理を呼び出す
    onPageChange(event, newPage);
  };

  const renderMobileView = () => {
    // 無限スクロール: 先頭から visibleCount 件を表示
    const visibleConstructions = constructions.slice(0, mobileVisibleCount);
    const hasMore = mobileVisibleCount < constructions.length;

    return (
      <Box>
        {visibleConstructions.map((construction, index) => (
          <Paper
            key={construction.ID}
            elevation={2}
            sx={{
              mb: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '4px solid',
              borderColor: 'primary.light',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            {/* クリック可能な領域を作成 */}
            <Box
              onClick={() => onToggleRowExpansion(index)}
              sx={{ cursor: 'pointer' }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* 会社名 */}
                <Typography variant="h6">
                  {construction.customerName || '-'}
                </Typography>

                {/* 工事内容を追加 */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {construction.constructionDetails || '(工事内容なし)'}
                </Typography>

                {/* ステータス、担当者、日報登録ボタンを横並びに */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',  // 位置の基準点として設定
                  minHeight: '40px',     // 最小の高さを確保
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flex: 1,
                    mr: '120px'  // 日報登録ボタンの幅分を確保
                  }}>
                    <Typography
                      variant="body2"
                      sx={{
                        // 背景色を除去し、文字色のみで表現
                        color: construction.isCompleted ? 'success.main' : 'warning.main',
                        border: 1,
                        borderColor: construction.isCompleted ? 'success.main' : 'warning.main',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        minWidth: '48px',
                        textAlign: 'center'
                      }}
                    >
                      {construction.isCompleted ? '完了' : '未完了'}
                    </Typography>
                    <Typography variant="body2">
                      担当: {construction.supervisor || '-'}
                    </Typography>
                  </Box>

                  {/* 日報登録ボタンを右端に固定 */}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (construction.ID) {
                        onDailyReportCreate(construction.ID);
                      }
                    }}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      minWidth: '100px',
                      fontWeight: 'bold',
                      boxShadow: 1,
                      '&:hover': {
                        boxShadow: 2,
                      }
                    }}
                  >
                    日報登録
                  </Button>
                </Box>
              </Box>

              {/* 下三角を中央に配置 */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 2
              }}>
                <ExpandMoreIcon
                  sx={{
                    transform: expandedRows.includes(index) ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.3s'
                  }}
                />
              </Box>
            </Box>

            <Collapse in={expandedRows.includes(index)}>
              <Box mt={2} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2">現場名: {construction.siteName || '-'}</Typography>
                <Typography variant="body2">工事番号: {construction.quotationNo || '-'}</Typography>

                {/* ボタン群を2段組みに変更 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* 1段目: 編集・削除・計算書 */}
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'space-between',
                    width: '100%'
                  }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        construction.ID && onEdit(construction.ID);
                      }}
                      sx={{
                        padding: '4px',
                        flex: 1,
                        height: '60px'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <EditIcon fontSize="small" sx={{ mb: 0.5 }} />
                        <Typography variant="caption" component="span">編集</Typography>
                      </Box>
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        construction.ID && onDelete(construction.ID);
                      }}
                      sx={{
                        padding: '4px',
                        flex: 1,
                        height: '60px'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <DeleteIcon fontSize="small" sx={{ mb: 0.5 }} />
                        <Typography variant="caption" component="span">削除</Typography>
                      </Box>
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      color="info"
                      onClick={(e) => {
                        e.stopPropagation();
                        construction.ID && onCalculationSheet(construction.ID);
                      }}
                      sx={{
                        padding: '4px',
                        flex: 1,
                        height: '60px'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ReceiptIcon fontSize="small" sx={{ mb: 0.5 }} />
                        <Typography variant="caption" component="span">計算書</Typography>
                      </Box>
                    </Button>
                  </Box>

                  {/* 2段目: 日報と請求依頼（工事完了時のみ） */}
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    width: '100%'
                  }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        construction.ID && handleViewReportList(construction.ID);
                      }}
                      sx={{
                        padding: '4px',
                        flex: 1,
                        height: '60px'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ListAltIcon fontSize="small" sx={{ mb: 0.5 }} />
                        <Typography variant="caption" component="span">日報</Typography>
                      </Box>
                    </Button>

                    {construction.isCompleted ? (
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          construction.ID && onInvoiceRequestClick(construction.ID);
                        }}
                        sx={{
                          padding: '4px',
                          flex: 1,
                          height: '60px'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <ReceiptIcon fontSize="small" sx={{ mb: 0.5 }} />
                          <Typography variant="caption" component="span">請求依頼</Typography>
                        </Box>
                      </Button>
                    ) : (
                      // 工事未完了の場合は空のスペースを確保するためのダミー要素
                      <Box sx={{ flex: 1 }} />
                    )}

                    {/* 3つ目のボタンがない場合のためのダミースペース */}
                    <Box sx={{ flex: 1 }} />
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Paper>
        ))}

        {/* 無限スクロール sentinel + ステータス表示 */}
        {hasMore ? (
          <Box ref={sentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          constructions.length > 0 && (
            <Typography
              variant="caption"
              sx={{ display: 'block', textAlign: 'center', py: 2, color: 'text.secondary' }}
            >
              全 {constructions.length} 件を表示しました
            </Typography>
          )
        )}
      </Box>
    );
  };


  const renderDesktopView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '60px' }}>工事完了</TableCell>
            <TableCell sx={{ width: '80px' }}>日報登録</TableCell>
            <TableCell>工事番号</TableCell>
            <TableCell>顧客名</TableCell>
            <TableCell>工事内容</TableCell>
            <TableCell>現場名</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>担当</TableCell>
            <TableCell align="center" sx={{ width: '48px' }}>編集</TableCell>
            <TableCell align="center" sx={{ width: '48px' }}>削除</TableCell>
            <TableCell align="center" sx={{ width: '56px' }}>計算書</TableCell>
            <TableCell align="center" sx={{ width: '64px' }}>日報一覧</TableCell>
            <TableCell align="center" sx={{ width: '64px' }}>請求依頼</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {constructions && constructions.length > 0 ? (
            constructions
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((construction) => (
                <TableRow key={construction.ID}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        display: 'inline-block',
                        bgcolor: construction.isCompleted ? 'success.light' : 'warning.light',
                        color: construction.isCompleted ? 'success.dark' : 'warning.dark',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        textAlign: 'center',
                        width: '100%'
                      }}
                    >
                      {construction.isCompleted ? '完了' : '未完了'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => {
                        if (construction.ID) {
                          onDailyReportCreate(construction.ID);
                        }
                      }}
                    >
                      日報登録
                    </Button>
                  </TableCell>
                  <TableCell>{construction.quotationNo || '-'}</TableCell>
                  <TableCell>{construction.customerName || '-'}</TableCell>
                  <TableCell>{construction.constructionDetails || '-'}</TableCell>
                  <TableCell>{construction.siteName || '-'}</TableCell>
                  <TableCell>{construction.supervisor || '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => construction.ID && onEdit(construction.ID)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => construction.ID && onDelete(construction.ID)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => construction.ID && onCalculationSheet(construction.ID)}
                      color="info"
                      size="small"
                    >
                      <ReceiptIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => construction.ID && handleViewReportList(construction.ID)}
                      color="secondary"
                      size="small"
                    >
                      <ListAltIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => construction.isCompleted && construction.ID && onInvoiceRequestClick(construction.ID)}
                      color="success"
                      size="small"
                      disabled={!construction.isCompleted}
                    >
                      <ReceiptIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
          ) : (
            <TableRow>
              <TableCell colSpan={11} align="center">
                データがありません
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );


  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Button
        variant="contained"
        color="primary"
        onClick={onNewClick}
        fullWidth={isSmallScreen}
        sx={{ mb: 2 }}
      >
        新規登録
      </Button>

      {/* ここに請求書依頼未作成の完了工事セクションを追加 */}
      <CompletedWorksWithoutInvoiceSection />
      
      {isSmallScreen ? renderMobileView() : renderDesktopView()}

      {/* PC のみ通常のページネーション。モバイルは無限スクロールで処理 */}
      {!isSmallScreen && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={constructions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      )}

      {/* 日報一覧モーダルを追加 */}
      <ReportListModal
        open={reportListModalOpen}
        constructionId={selectedConstructionId}
        onClose={() => setReportListModalOpen(false)}
        onEditReport={onEditReport}
      />
    </Box>
  );
};